/**
 * useSidebarSubscriptions Hook
 *
 * Subscribes to all active mission executions via the streaming WebSocket
 * so the sidebar can show running indicators, "needs input" badges, and
 * governance alerts in real time.
 *
 * F1 (chat-ui catch-up plan): the streaming-service hub requires an
 * execution_id on every `mission.subscribe` (see
 * streaming-service/internal/relay/client.go:307-313). The sidebar therefore
 * subscribes per (mission, active execution) pair — a single mission with
 * two concurrent executions opens two subscriptions, and the per-mission
 * "running" badge stays lit until BOTH end (refcount in useExecutionStore).
 *
 * Source of truth for which executions exist: useConversationHistory, which
 * fetches GET /v1/executions and groups by mission. Whenever that data
 * refreshes, this hook reconciles its subscription set: subscribe to any
 * (mission, execution) that has appeared, unsubscribe from any that has
 * gone.
 *
 * The run list's scope (persona F-ACC-03, agion/Agion#3003) comes from the
 * caller: the new chat's sidebar subscribes only to own and invited runs, so
 * another person's run raises no question, alert or badge there. Without a
 * scope the list is read as before.
 */

import { useEffect, useRef, useMemo } from 'react'
import { getMissionStatusService } from '../services/streaming/mission-status'
import { useMissionStore } from './useMissions'
import { isListedMission } from '../utils/missionListing'
import { useExecutionStore } from './useExecutionStore'
import { useConversationHistory } from './useConversationHistory'
import type { ExecutionListScope } from '../services/api'
import type { MissionSubscriptionOptions } from '../types/streaming'

/** Composite key for the subscription map. Two executions of the same mission
 *  must be distinct entries. */
function subKey(missionId: string, executionId: string): string {
  return `${missionId} ${executionId}`
}

type SubscriptionRecord = {
  missionId: string
  executionId: string
  unsub: () => void
}

export interface UseSidebarSubscriptionsOptions {
  /** The server scope of the run list the subscriptions come from; none reads as before. */
  scope?: ExecutionListScope
}

export function useSidebarSubscriptions(options: UseSidebarSubscriptionsOptions = {}): void {
  const { missions } = useMissionStore()
  const { groupedByMission } = useConversationHistory({ scope: options.scope })
  const {
    addHITLRequest,
    removeHITLRequest,
    removeHITLRequestsForMission,
    addGovernanceAlert,
    removeGovernanceAlertsForMission,
    addRunningExecution,
    removeRunningExecution,
  } = useExecutionStore()

  // Composite-key map of live subscriptions.
  const subsRef = useRef<Map<string, SubscriptionRecord>>(new Map())

  // The missions the sidebar lists (utils/missionListing); keep a stable name
  // map so the badge state can carry missionName.
  const missionNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const mission of missions) {
      if (!isListedMission(mission)) continue
      m.set(mission.id, mission.name || mission.title || '')
    }
    return m
  }, [missions])

  // Active (running) executions per mission, derived from conversation history.
  const activeExecutions = useMemo(() => {
    const out: Array<{ missionId: string; missionName: string; executionId: string }> = []
    for (const group of groupedByMission) {
      if (!missionNameById.has(group.missionId)) continue
      for (const exec of group.executions) {
        if (!exec.isActive) continue
        out.push({
          missionId: group.missionId,
          missionName: missionNameById.get(group.missionId) ?? group.missionName,
          executionId: exec.id,
        })
      }
    }
    return out
  }, [groupedByMission, missionNameById])

  useEffect(() => {
    const service = getMissionStatusService()
    const currentSubs = subsRef.current

    const wanted = new Set(activeExecutions.map((e) => subKey(e.missionId, e.executionId)))

    // Drop subscriptions that are no longer wanted (execution ended).
    for (const [key, record] of currentSubs) {
      if (!wanted.has(key)) {
        record.unsub()
        removeRunningExecution(record.missionId, record.executionId)
        currentSubs.delete(key)
      }
    }

    // Open subscriptions for newly-active executions.
    for (const target of activeExecutions) {
      const key = subKey(target.missionId, target.executionId)
      if (currentSubs.has(key)) continue

      const { missionId, missionName, executionId } = target

      // Mark this execution as running immediately — the WS subscribe is async
      // and we want the badge to light up without waiting for the first event.
      addRunningExecution(missionId, executionId)

      const options: MissionSubscriptionOptions = {
        onNodeStatusUpdate: (update) => {
          if (update.status === 'running') {
            addRunningExecution(missionId, executionId)
          }
        },
        onHITLRequest: (request) => {
          // Skip display-only HITL messages (not actionable).
          if (request.modality === 'message') return
          addHITLRequest({
            requestId: request.request_id,
            missionId,
            missionName,
            executionId: request.execution_id,
            nodeId: request.node_id,
            agentId: request.agent_id,
            prompt: request.prompt,
            options: request.options,
            modality: request.modality,
            timeoutMs: request.timeout_ms,
            receivedAt: Date.now(),
          })
        },
        onHITLResponse: (response) => {
          removeHITLRequest(response.request_id)
        },
        onGovernanceDecision: (decision) => {
          if (decision.action === 'deny' || decision.action === 'escalate') {
            addGovernanceAlert({
              id: `gov-${decision.node_id}-${Date.now()}`,
              missionId,
              missionName,
              nodeId: decision.node_id,
              action: decision.action as 'deny' | 'escalate',
              reason: decision.reason,
              ruleName: decision.rule_name,
              receivedAt: Date.now(),
            })
          }
        },
        onCompleted: () => {
          removeRunningExecution(missionId, executionId)
          // HITL/governance are mission-scoped (not execution-scoped) in the
          // store; clearing them on the last execution is the existing
          // behavior and matches the badge UX.
          removeHITLRequestsForMission(missionId)
          removeGovernanceAlertsForMission(missionId)
        },
        onFailed: () => {
          removeRunningExecution(missionId, executionId)
          removeHITLRequestsForMission(missionId)
          removeGovernanceAlertsForMission(missionId)
        },
      }

      // Fire-and-forget; the promise resolves to the unsubscribe fn that we
      // store for later cleanup. If subscribeAsync rejects, we drop the entry
      // so the next reconciliation can retry.
      service
        .subscribeAsync(missionId, executionId, options)
        .then((unsub) => {
          currentSubs.set(key, { missionId, executionId, unsub })
        })
        .catch(() => {
          removeRunningExecution(missionId, executionId)
        })
    }

    // Cleanup on unmount: drop ALL subscriptions.
    return () => {
      for (const [, record] of currentSubs) {
        record.unsub()
      }
      currentSubs.clear()
    }
  }, [
    activeExecutions,
    addHITLRequest,
    removeHITLRequest,
    removeHITLRequestsForMission,
    addGovernanceAlert,
    removeGovernanceAlertsForMission,
    addRunningExecution,
    removeRunningExecution,
  ])
}
